'use client';

import { captureException as capturePostHogException } from '@/lib/analytics/posthog';
import { useEffect } from 'react';

/**
 * Error boundary for App Router
 * Catches errors in route components and captures them in PostHog
 * 
 * Place this in any route directory to handle errors for that route
 * @see https://nextjs.org/docs/app/building-your-application/routing/error-handling
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Capture exception in PostHog with enhanced metadata
    capturePostHogException(error, {
      $exception_type: error.name,
      $exception_message: error.message,
      $exception_digest: error.digest,
      $exception_source: 'client',
      $exception_level: 'error',
      // Capture current page context
      $current_url: typeof window !== 'undefined' ? window.location.href : undefined,
      $pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
    });
  }, [error]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Something went wrong</h1>
      <p style={{ fontSize: '1.125rem', marginBottom: '2rem', color: '#666' }}>
        An unexpected error occurred. We&apos;re working on fixing it.
      </p>
      <button
        onClick={reset}
        style={{
          padding: '0.75rem 1.5rem',
          fontSize: '1rem',
          backgroundColor: '#000',
          color: '#fff',
          border: 'none',
          borderRadius: '0.5rem',
          cursor: 'pointer',
        }}
      >
        Try again
      </button>
    </div>
  );
}
